import { Monitor, Moon, Sun } from "lucide-react";
import { Button } from "../components/button";
import { cn } from "../lib/cn";
import { type ThemeChoice, useTheme } from "./use-theme";

/** The order the mark steps through. Pressing from the last comes back here. */
const CHOICES = [
  { value: "system", name: "the system" },
  { value: "light", name: "light" },
  { value: "dark", name: "dark" },
] as const satisfies readonly { value: ThemeChoice; name: string }[];

const rises = "transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]";

/**
 * Light, dark, or whatever the system is doing, behind one press and no label.
 *
 * Three settings laid out as three controls is more furniture than a single
 * preference deserves, so the mark shows the one you are on and pressing it
 * steps to the next.
 *
 * All three are always there, stacked in one small window that clips them.
 * The sun and the moon keep to the horizon, each turning a quarter and
 * scaling away as the other comes upright; the screen has no horizon to
 * cross, so it drops out of the bottom of the window and climbs back in.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { choice, chooseTheme } = useTheme();
  const chosen = CHOICES.findIndex(option => option.value === choice);
  const next = CHOICES[(chosen + 1) % CHOICES.length];

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={`Theme: ${CHOICES[chosen].name}. Press for ${next.name}.`}
      onClick={() => chooseTheme(next.value)}
      className={cn("relative text-muted-foreground hover:text-foreground", className)}
    >
      <span className="grid size-4 overflow-hidden">
        <Monitor
          aria-hidden
          className={cn(
            "col-start-1 row-start-1",
            rises,
            choice === "system" ? "translate-y-0" : "translate-y-full",
          )}
        />
        <Sun
          aria-hidden
          className={cn(
            "col-start-1 row-start-1",
            rises,
            choice === "light" ? "rotate-0 scale-100" : "rotate-90 scale-0",
          )}
        />
        <Moon
          aria-hidden
          className={cn(
            "col-start-1 row-start-1",
            rises,
            choice === "dark" ? "rotate-0 scale-100" : "-rotate-90 scale-0",
          )}
        />
      </span>
    </Button>
  );
}
